import { 
  useState, 
  useRef, 
  useEffect, 
  useCallback, 
  useContext,
  memo 
} from "react";

import PropTypes from "prop-types";

import { sendCopilotMessage } from "../../../model/services/Copilot";

import { TenantContext } from '../../../templates/context/TenantContext'

const MAX_MESSAGE_LENGTH = 500;

const SUGGESTED_QUESTIONS = [
  "Quais subgrupos concentram o maior valor de oportunidades neste OTB?",
  "Onde a nova tendência de vendas está acima do forecast há mais de 4 semanas?",
  "Quanto eu deixo de faturar se não ajustar o plano de Bottom Jeans Longo?",
];

const CopilotComponent = ({ ctx }) => {
  const { userInfo } = useContext(TenantContext);
  const { tenant } = userInfo || {};
  const { name } = tenant || {};

  const { userId, tenantId } = ctx || {};

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState(null);

  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (event.target.closest("[data-copilot-trigger]")) return;
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      const userMessage = { id: `${Date.now()}-user`, role: "user", content: trimmed };
      setMessages((prev) => [...prev, userMessage]);
      setInputValue("");
      setError(null);
      setIsLoading(true);

      const data = await sendCopilotMessage({ message: trimmed, userId, tenantId });

      if (data.success === false) {
        setError("Não foi possível obter uma resposta agora. Tente novamente.");
      } else {
        setMessages((prev) => [
          ...prev,
          { id: `${Date.now()}-assistant`, role: "assistant", content: data.answer },
        ]);
      }

      setIsLoading(false);
    },
    [userId, tenantId, isLoading]
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(inputValue);
  };

  const handleSuggestionClick = (question) => {
    sendMessage(question);
  };

  const handleNewConversation = () => {
    setMessages([]);
    setInputValue("");
    setError(null);
  };

  if (name?.toLowerCase() !== 'demo') return null;

  return (
    <>
      <button
        type="button"
        className="copilot-trigger-button"
        data-copilot-trigger
        onClick={() => setIsOpen((prev) => !prev)}>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z"
            fill="currentColor"
          />
          <path
            d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z"
            fill="currentColor"
          />
        </svg>
        <span>Copilot Aidron</span>
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          className={`copilot-panel ${isExpanded ? "copilot-panel--expanded" : ""}`}>
          <header className="copilot-header">
            <div className="copilot-header__title">
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                    d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z"
                    fill="currentColor"
                    />
                    <path
                    d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z"
                    fill="currentColor"
                    />
                </svg>
                
                <span>Copilot Aidron</span>
            </div>

            <div className="copilot-header__actions">
              <button
                type="button"
                className="copilot-icon-button"
                onClick={handleNewConversation}
                aria-label="Nova conversa">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                        d="M21 11.5c0 4.14-4.03 7.5-9 7.5-1.06 0-2.08-.15-3.02-.43L4 20l1.1-3.3A7.13 7.13 0 013 11.5C3 7.36 7.03 4 12 4s9 3.36 9 7.5z"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinejoin="round" />
                    <path d="M12 8.5v5M9.5 11h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>

              <button
                type="button"
                className="copilot-icon-button"
                onClick={() => setIsExpanded((prev) => !prev)}
                aria-label="Expandir">

                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                    d="M9 3H3v6M15 3h6v6M21 15v6h-6M3 15v6h6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    />
                </svg>
              </button>

              <button
                type="button"
                className="copilot-icon-button"
                onClick={() => setIsOpen(false)}
                aria-label="Fechar">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>            
              </button>
            </div>
          </header>

          <div className="copilot-body">
            {messages.length === 0 ? (
              <>
                <div className="copilot-welcome">
                  <div className="copilot-welcome__icon">
                    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                        d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z"
                        fill="currentColor"
                        />
                        <path
                        d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z"
                        fill="currentColor"
                        />
                    </svg>

                  </div>
                  <h3 className="copilot-welcome__title">Modo Analista</h3>
                  <p className="copilot-welcome__description">
                    Precisa de ajuda para entender as oportunidades ou identificar tendências? Estou
                    aqui para te ajudar.
                  </p>
                </div>

                <div className="copilot-suggestions">
                  {SUGGESTED_QUESTIONS.map((question) => (
                    <button
                      key={question}
                      type="button"
                      className="copilot-suggestion"
                      onClick={() => handleSuggestionClick(question)}
                    >
                      <span className="copilot-suggestion__label">
                        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                            d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z"
                            fill="currentColor"
                            />
                            <path
                            d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z"
                            fill="currentColor"
                            />
                        </svg> 
                        PERGUNTAR AO COPILOT
                      </span>
                      <span className="copilot-suggestion__text">{question}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="copilot-messages">
                {messages.map((message) => (
                  <div key={message.id} className={`copilot-message copilot-message--${message.role}`}>
                    {message.content}
                  </div>
                ))}
                {isLoading && (
                  <div className="copilot-message copilot-message--assistant copilot-message--loading">
                    Digitando...
                  </div>
                )}
              </div>
            )}

            {error && <div className="copilot-error">{error}</div>}
          </div>

          <form className="copilot-input-bar" onSubmit={handleSubmit}>
            <textarea
              className="copilot-input"
              placeholder="Pergunte sobre as oportunidades..."
              value={inputValue}
              maxLength={MAX_MESSAGE_LENGTH}
              rows={1}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  handleSubmit(event);
                }
              }}
            />
            <span className="copilot-char-count">
              {inputValue.length}/{MAX_MESSAGE_LENGTH}
            </span>
            <button
              type="submit"
              className="copilot-send-button"
              disabled={!inputValue.trim() || isLoading}
              aria-label="Enviar">

            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M3 11l18-8-8 18-2-8-8-2z" fill="currentColor" />
            </svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
};

CopilotComponent.propTypes = {
  ctx: PropTypes.shape({
    userId: PropTypes.string,
    tenantId: PropTypes.string,
  }),
};

export const Copilot = memo(CopilotComponent);